<template>
  <div class="sah-page">
    <div class="sah-header">
      <h1 class="sah-title">營業時間</h1>
    </div>

    <div v-if="loading" class="sah-state">
      <span class="sah-spinner" /> 載入中…
    </div>
    <div v-else-if="loadError" class="sah-alert sah-alert--error">{{ loadError }}</div>

    <template v-else>
      <!-- 新手引導橫幅 -->
      <div v-if="showOnboard" class="sah-onboard-bar">
        <div class="sah-onboard-bar__steps">
          <span class="sah-onboard-bar__step">① 店家資料 ✓</span>
          <span class="sah-onboard-bar__sep">›</span>
          <span class="sah-onboard-bar__step sah-onboard-bar__step--active">② 營業時間</span>
          <span class="sah-onboard-bar__sep">›</span>
          <span class="sah-onboard-bar__step">③ 服務設定</span>
          <span class="sah-onboard-bar__sep">›</span>
          <span class="sah-onboard-bar__step">④ 新增時段</span>
        </div>
        <p class="sah-onboard-bar__hint">請設定您的店家營業時間，完成後點「儲存營業時間」繼續下一步</p>
      </div>

      <div class="sah-card">
        <p class="sah-hint">設定後將顯示在前台店家頁面，讓客人了解營業資訊。</p>

        <div class="sah-rows">
          <div v-for="row in form" :key="row.day_of_week" class="sah-row">
            <!-- 星期 -->
            <span class="sah-day" :class="{ 'sah-day--closed': row.is_closed }">
              {{ DAY_LABELS[row.day_of_week] }}
            </span>

            <!-- 公休切換 -->
            <label class="sah-toggle">
              <input type="checkbox" v-model="row.is_closed" />
              <span class="sah-toggle__track" />
              <span class="sah-toggle__label">{{ row.is_closed ? '公休' : '營業' }}</span>
            </label>

            <!-- 時間輸入 -->
            <template v-if="!row.is_closed">
              <select v-model="row.open_time" class="sah-time-select">
                <option v-for="t in openTimeOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
              <span class="sah-sep">–</span>
              <select v-model="row.close_time" class="sah-time-select">
                <option v-for="t in closeTimeOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
            </template>
            <span v-else class="sah-closed-label">休息中</span>
          </div>
        </div>

        <p v-if="saveError" class="sah-alert sah-alert--error">{{ saveError }}</p>
        <p v-if="saveOk" class="sah-alert sah-alert--success">✓ 儲存成功</p>

        <!-- 儲存後：下一步引導 -->
        <div v-if="showNextStep" class="sah-next-step">
          <span class="sah-next-step__ok">✅ 已儲存！</span>
          <span class="sah-next-step__label">請點左上角選單繼續下一步</span>
        </div>

        <div class="sah-footer">
          <button
            class="sah-btn sah-btn--primary"
            :disabled="saving"
            @click="save"
          >
            <span v-if="saving" class="sah-spinner sah-spinner--sm sah-spinner--white" />
            {{ saving ? '儲存中…' : '儲存營業時間' }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import axios from 'axios'
import { useStoreAdminStore } from '@/store/storeAdminStore'

const storeAdminStore = useStoreAdminStore()
const updateObStep = inject('updateObStep', () => {})
const onboardKey   = computed(() => `jili_ob_h_${storeAdminStore.storeId || ''}`)
const showOnboard  = ref(false)
const showNextStep = ref(false)

const DAY_LABELS = ['週日', '週一', '週二', '週三', '週四', '週五', '週六']

// 每 30 分鐘一個選項
// 開始時間：00:00 ~ 23:30（value = "HH:MM"）
// 結束時間：00:30 ~ 29:30（24+ 表示隔日，value = "HH:MM"，label 顯示「隔日 HH:MM」）
function buildTimeOptions(maxHour = 24) {
  return Array.from({ length: maxHour * 2 }, (_, i) => {
    const totalMins = (i + 1) * 30
    const h = Math.floor(totalMins / 60)
    const m = totalMins % 60 === 0 ? '00' : '30'
    const value = `${String(h).padStart(2, '0')}:${m}`
    const label = h >= 24
      ? `隔日 ${String(h - 24).padStart(2, '0')}:${m}`
      : value
    return { value, label }
  })
}
const openTimeOptions  = buildTimeOptions(24).filter(t => {
  const [h] = t.value.split(':').map(Number)
  return h < 24
})
const closeTimeOptions = buildTimeOptions(30) // 最晚到隔日 06:00

const loading   = ref(false)
const loadError = ref('')
const saving    = ref(false)
const saveError = ref('')
const saveOk    = ref(false)

// 7 天預設值
const defaultForm = () =>
  Array.from({ length: 7 }, (_, i) => ({
    day_of_week: i,
    open_time:   '09:00',
    close_time:  '18:00',
    is_closed:   i === 0,   // 週日預設公休
  }))

const form = ref(defaultForm())

function authHeader() {
  return { Authorization: `Bearer ${storeAdminStore.token}` }
}

async function load() {
  loading.value   = true
  loadError.value = ''
  try {
    const res = await axios.get('/api/store-admin/hours', { headers: authHeader() })
    if (res.data.success && res.data.hours.length > 0) {
      // 用 DB 資料填入，沒設定的天保留預設值
      const map = {}
      res.data.hours.forEach(h => { map[h.day_of_week] = h })
      form.value = defaultForm().map(d => {
        const db = map[d.day_of_week]
        if (!db) return d
        return {
          day_of_week: d.day_of_week,
          open_time:   db.open_time  ? db.open_time.slice(0, 5)  : '09:00',
          close_time:  db.close_time ? db.close_time.slice(0, 5) : '18:00',
          is_closed:   !!db.is_closed,
        }
      })
    }
    if (!localStorage.getItem(onboardKey.value)) showOnboard.value = true
  } catch (e) {
    loadError.value = e.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value    = true
  saveError.value = ''
  saveOk.value    = false

  // 驗證：營業日必須有完整時間
  for (const row of form.value) {
    if (!row.is_closed && (!row.open_time || !row.close_time)) {
      saveError.value = `${DAY_LABELS[row.day_of_week]} 請填寫完整的開始與結束時間`
      saving.value = false
      return
    }
    if (!row.is_closed && row.open_time >= row.close_time) {
      saveError.value = `${DAY_LABELS[row.day_of_week]} 結束時間必須晚於開始時間（可選隔日時間）`
      saving.value = false
      return
    }
  }

  try {
    await axios.put(
      '/api/store-admin/hours',
      { hours: form.value },
      { headers: authHeader() }
    )
    saveOk.value = true
    localStorage.setItem(onboardKey.value, '1')
    showOnboard.value  = false
    showNextStep.value = true
    updateObStep()
    setTimeout(() => { saveOk.value = false }, 3000)
  } catch (e) {
    saveError.value = e.response?.data?.message || '儲存失敗，請稍後再試'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
/* ── 新手引導 ──────────────────────────────────────── */
.sah-onboard-bar {
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  border: 1.5px solid #93c5fd;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
  max-width: 640px;
}
.sah-onboard-bar__steps {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.sah-onboard-bar__step { font-size: 0.82rem; color: #93c5fd; font-weight: 600; }
.sah-onboard-bar__step--active { color: #1d4ed8; }
.sah-onboard-bar__sep { color: #bfdbfe; font-size: 0.9rem; }
.sah-onboard-bar__hint { font-size: 0.88rem; color: #1e40af; margin: 0; }
.sah-next-step {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  background: #f0fdf4; border: 1px solid #86efac;
  border-radius: 8px; padding: 12px 16px; margin-bottom: 12px;
}
.sah-next-step__ok    { color: #16a34a; font-weight: 700; font-size: 0.92rem; }
.sah-next-step__label { font-size: 0.88rem; color: #374151; }
.sah-next-step__btn {
  background: #2563eb; color: #fff; border-radius: 6px;
  padding: 6px 14px; font-size: 0.88rem; font-weight: 600;
  text-decoration: none; transition: background 0.15s; white-space: nowrap;
}
.sah-next-step__btn:hover { background: #1d4ed8; }

.sah-page {
  padding: 24px;
  max-width: 640px;
}

.sah-header {
  margin-bottom: 24px;
}
.sah-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.sah-state {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #7f8c8d;
  padding: 40px 0;
}

.sah-card {
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.sah-hint {
  font-size: 0.87rem;
  color: #7f8c8d;
  margin: 0 0 20px;
}

.sah-rows {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sah-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
}

.sah-day {
  width: 36px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  flex-shrink: 0;
}
.sah-day--closed {
  color: #aaa;
}

/* Toggle */
.sah-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.sah-toggle input { display: none; }
.sah-toggle__track {
  width: 42px;
  height: 24px;
  border-radius: 12px;
  background: #dce1e7;
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
}
.sah-toggle__track::after {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  top: 3px;
  left: 3px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.sah-toggle input:checked ~ .sah-toggle__track {
  background: #27ae60;
}
.sah-toggle input:checked ~ .sah-toggle__track::after {
  transform: translateX(18px);
}
/* 公休時 track 顏色 */
.sah-toggle input:not(:checked) ~ .sah-toggle__track {
  background: #e74c3c;
}
.sah-toggle input:not(:checked) ~ .sah-toggle__track::after {
  transform: translateX(0);
}
.sah-toggle__label {
  font-size: 0.82rem;
  font-weight: 600;
  width: 28px;
  color: #555;
}

/* 時間下拉選單 */
.sah-time-select {
  padding: 6px 8px;
  border: 1px solid #dce1e7;
  border-radius: 7px;
  font-size: 0.9rem;
  background: #fff;
  color: #2c3e50;
  width: 90px;
  box-sizing: border-box;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  text-align: center;
}
.sah-time-select:focus {
  outline: none;
  border-color: #2c3e50;
  box-shadow: 0 0 0 3px rgba(44,62,80,0.07);
}
.sah-sep {
  color: #aaa;
  font-size: 1rem;
}
.sah-closed-label {
  font-size: 0.82rem;
  color: #aaa;
  margin-left: 8px;
}

/* 警示 */
.sah-alert {
  border-radius: 7px;
  padding: 10px 14px;
  font-size: 0.87rem;
  margin-top: 16px;
}
.sah-alert--error   { background: #fdedec; border: 1px solid #f5b7b1; color: #c0392b; }
.sah-alert--success { background: #eafaf1; border: 1px solid #a9dfbf; color: #1e8449; }

/* Footer */
.sah-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 按鈕 */
.sah-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.sah-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.sah-btn--primary { background: #2c3e50; color: #fff; }
.sah-btn--primary:hover:not(:disabled) { background: #34495e; }

/* Spinner */
.sah-spinner {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 3px solid rgba(0,0,0,0.1);
  border-top-color: #7f8c8d;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.sah-spinner--sm    { width: 14px; height: 14px; border-width: 2px; }
.sah-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

/* RWD */
@media (max-width: 520px) {
  .sah-page { padding: 14px; }
  .sah-row  { flex-wrap: wrap; gap: 8px; }
  .sah-time { width: 90px; }
}
</style>
